'use client'

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MeetingNotesDocumentBody } from './meeting-notes-document-body'
import { buildTemplatePreview, TEMPLATE_PROBLEM_REASON_KEYS } from './template-preview-model'
import './template-preview.css'

export interface TemplatePreviewProps {
  markdown: string
}

/**
 * What this template will actually look like, rendered while you type.
 *
 * Used by BOTH 整理範本 drawers — `/admin` built-ins and the user's own. One
 * component rather than two, because the thing being previewed is the same
 * document in both places, and a second copy is how the two would start
 * disagreeing about what a template renders as.
 *
 * It renders through the real pipeline (markdown → doc → `renderMeetingNotesHtml` →
 * `MeetingNotesDocumentBody` in read-only mode, same stylesheet), so what shows
 * here is what `/app` shows — a preview that approximated the output would just
 * move the surprise, not remove it.
 *
 * The problem list exists because the rendering alone cannot say WHY. A fake
 * table renders as a perfectly ordinary paragraph; without the line number and
 * the reason, the author sees something that looks fine-ish and saves it.
 */
export function TemplatePreview({ markdown }: TemplatePreviewProps) {
  const { t } = useTranslation('web')
  const preview = useMemo(() => buildTemplatePreview(markdown), [markdown])

  return (
    <div className="tpl-preview">
      <span className="tpl-preview-label">{t('notesTemplatePreview.label')}</span>

      {preview.problems.length > 0 ? (
        <div className="tpl-preview-problems" role="alert">
          <b>{t('notesTemplatePreview.problemsTitle')}</b>
          <ul>
            {preview.problems.map((problem) => (
              <li key={`${problem.line}-${problem.reason}`}>
                {t('notesTemplatePreview.problemItem', {
                  line: problem.line,
                  reason: t(TEMPLATE_PROBLEM_REASON_KEYS[problem.reason]),
                })}
              </li>
            ))}
          </ul>
          <span className="hint">{t('notesTemplatePreview.fixHint')}</span>
        </div>
      ) : null}

      <div className="tpl-preview-frame">
        {preview.isEmpty ? (
          <p className="tpl-preview-empty">{t('notesTemplatePreview.empty')}</p>
        ) : (
          <MeetingNotesDocumentBody contentHtml={preview.html} editable={false} />
        )}
      </div>
    </div>
  )
}
